import { Color, ColorMode } from '@color';
import type { Matrix } from '@geom';
import {
  beautifySvg,
  composeSvgPathData,
  LineSegment,
  optimizeSvg,
  Point,
  Rect,
  SERIALIZATION_PRECISION,
  stripRootAndPrefixClasses,
  SvgOutputMode
} from '@geom';
import type { Image } from '@image';
import { calculateObjectFitLayout, ObjectFit, SVGImageSource } from '@image';
import { determineBaseTextDirection, TextDirection } from '@strings';
import {
  Defs,
  Style,
  SVG,
  Container as SvgContainer,
  Dom as SvgDom,
  Element as SvgElement,
  Path as SvgPath,
  Rect as SvgRect,
  type FillData,
  type StrokeData
} from '@svgdotjs/svg.js';
import type { Paragraph } from '@typography';
import { BasicTextStyle } from '@typography';
import {
  encodeBase64,
  expectDefined,
  fixedFloat,
  fixedNumber,
  FrequencyCounter,
  newFuid
} from '@utils';
import {
  isUniformCornerRadius,
  isZeroCornerRadius,
  normalizeCornerRadius,
  type CornerRadiusOpts,
  type NormalizedCornerRadiusOpts
} from './cornerRadius';
import type { Shape } from './Shape';
import { FontExportMethod, VectorExportFont } from './VectorExportFont';
import type { DrawImageOpts } from './Visual';
import {
  DEFAULT_DPR,
  PaintStyle,
  StrokeCap,
  Visual,
  VisualPaint,
  type DrawRectOpts,
  type VisualGroupOpts
} from './Visual';
import { VisualCache } from './VisualCache';

const SVG_PRECISION = 2;

export interface SvgOpts {
  idPrefix?: string;
  fontExportMethod: FontExportMethod;
  useMonochromeEmoji: boolean;
  outputMode: SvgOutputMode;
  devicePixelRatio: number;
}

export const defaultSvgOpts: SvgOpts = {
  fontExportMethod: FontExportMethod.PATHS,
  useMonochromeEmoji: false,
  outputMode: SvgOutputMode.BEAUTIFIED,
  devicePixelRatio: DEFAULT_DPR
};

export class SVGVisual extends Visual {
  idPrefix: string;
  bounds: Rect;
  draw: SvgContainer;
  mainGroup: SvgContainer;
  _defs: Defs | undefined;
  _style: Style | undefined;
  fontExportMethod: FontExportMethod;
  useMonochromeEmoji: boolean;
  outputMode: SvgOutputMode;
  usedFonts = new Map<string, VectorExportFont>();

  // Image deduplication properties
  private imageDefsMap = new Map<Image, string>();
  private imageDefsCounter = 0;

  // SVG content deduplication properties
  private svgDefsMap = new Map<string, string>();
  private svgDefsCounter = 0;
  private clipPathCounter = 0;

  constructor(
    colorMode: ColorMode,
    cache: VisualCache,
    bounds: Rect,
    opts?: Partial<SvgOpts>
  ) {
    const { fontExportMethod, useMonochromeEmoji, outputMode, devicePixelRatio } = {
      ...defaultSvgOpts,
      ...opts
    };
    super(colorMode, cache, devicePixelRatio);
    if (opts?.idPrefix === undefined) {
      this.idPrefix = newFuid();
    } else {
      this.idPrefix = opts.idPrefix;
    }
    this.bounds = bounds;
    this.draw = SVG()
      .size(bounds.width, bounds.height)
      .viewbox(bounds.left, bounds.top, bounds.width, bounds.height);
    // .attr('style', 'overflow: visible;');

    // Initialize mainGroup to draw - it will be reassigned during withGroup operations
    this.mainGroup = this.draw;

    this.outputMode = outputMode;
    this.fontExportMethod = fontExportMethod;
    this.useMonochromeEmoji = useMonochromeEmoji;
  }

  get defs(): Defs {
    if (this._defs === undefined) {
      this._defs = this.draw.defs();
    }
    return this._defs;
  }

  get style(): Style {
    if (this._style === undefined) {
      this._style = this.defs.style();
    }
    return this._style;
  }

  getUsedFont(family: string): VectorExportFont {
    let usedFont = this.usedFonts.get(family);
    if (usedFont === undefined) {
      usedFont = new VectorExportFont(family, this.fontExportMethod);
      this.usedFonts.set(family, usedFont);
    }
    return usedFont;
  }

  addStringToSubset(family: string, s: string) {
    this.getUsedFont(family).addStringToSubset(s);
  }

  addPathForString(
    family: string,
    s: string,
    position: Point,
    size: number,
    context: TextDirection,
    weight?: number,
    italic?: boolean
  ): SvgPath | undefined {
    return this.getUsedFont(family).addPathForString(s, position, size, context, {
      weight,
      italic
    });
  }

  referenceFont(family: string): void {
    this.getUsedFont(family).needsFontFaceRule = true;
  }

  getSvg(): string {
    const fontFamilies = Array.from(this.usedFonts.keys());
    const rules: string[] = [];
    for (const family of fontFamilies) {
      const usedFont = expectDefined(this.usedFonts.get(family));
      if (!usedFont.needsFontFaceRule) {
        continue;
      }
      if (this.fontExportMethod === FontExportMethod.EMBED) {
        try {
          // This fails for Noto Color Emoji.
          const binarySubset = usedFont.subset.encode();

          const base64Subset = encodeBase64(binarySubset);
          const url = `data:font/opentype;base64,${base64Subset} format('opentype')`;
          rules.push(`@font-face { font-family: '${family}'; src: url('${url}'); }`);
        } catch {
          console.error(`Failed to encode subset for font: "${family}"`);
        }
      }
    }
    if (rules.length > 0) {
      this.style.addText(rules.join('\n'));
    }

    const svg = this.draw.svg((node: SvgDom) => node.round(2));
    switch (this.outputMode) {
      case SvgOutputMode.BEAUTIFIED:
        return beautifySvg(svg);
      case SvgOutputMode.OPTIMIZED:
        return optimizeSvg(svg);
      case SvgOutputMode.NORMAL:
      default:
        return svg;
    }
  }

  drawParagraph(paragraph: Paragraph, bounds: Rect, point: Point, opacity: number): void {
    const paragraphText = paragraph.text;
    const baseTextDirection = determineBaseTextDirection(paragraphText);
    const direction = baseTextDirection == TextDirection.RTL ? 'rtl' : undefined;
    const textAnchor = baseTextDirection == TextDirection.RTL ? 'end' : undefined;
    const basicStyle = paragraph.measuredText.opts.basicTextStyle;
    const isBold =
      basicStyle === BasicTextStyle.BOLD || basicStyle === BasicTextStyle.BOLD_ITALIC;
    const isItalic =
      basicStyle === BasicTextStyle.ITALIC || basicStyle === BasicTextStyle.BOLD_ITALIC;
    const weight = isBold ? 700 : 400;

    const colorCounts = new FrequencyCounter<Color>();
    paragraph.runs.forEach(run => {
      colorCounts.add(new Color(run.color));
    });

    const defaultColor = expectDefined(colorCounts.top);
    const opacityAttr = opacity < 1 ? fixedFloat(opacity, SVG_PRECISION) : undefined;
    const group = this.mainGroup.group().attr({
      opacity: opacityAttr,
      transform: `translate(${fixedNumber(point.x, SVG_PRECISION)}, ${fixedNumber(point.y, SVG_PRECISION)})`,
      fill: defaultColor.toHexRGB()
    });

    if (this.fontExportMethod === FontExportMethod.EMBED) {
      paragraph.runs.forEach(run => {
        this.addStringToSubset(run.familyName, run.text);
      });
    }

    if (this.fontExportMethod === FontExportMethod.PATHS) {
      paragraph.runs.forEach(run => {
        const text = run.text;

        const position = new Point(run.position);
        const c = new Color(run.color);
        const color = c.equals(defaultColor) ? undefined : c;

        let family: string = run.familyName;
        if (this.useMonochromeEmoji && family.toLowerCase().includes('emoji')) {
          family = 'Noto Emoji';
        }
        const size = run.size;

        // Map style from paragraph-level basic style for consistent SVG output
        let svgElement: SvgElement | undefined = this.addPathForString(
          family,
          text,
          position,
          size,
          baseTextDirection,
          weight,
          isItalic
        );

        if (svgElement !== undefined) {
          group
            .element('desc')
            .words(
              `"${text.trim()}" "${family}" ${fixedNumber(size, SVG_PRECISION)}, x: ${fixedNumber(position.x, SVG_PRECISION)}, y: ${fixedNumber(position.y, SVG_PRECISION)}`
            );
          group.add(svgElement);
        } else {
          this.referenceFont(family);
          // If a path could not be created for the string, fall back to plain text.
          const familyWithFallbacks = this.getUsedFont(family).familyWithFallbacks;
          svgElement = group
            .plain(text)
            .font({ family: familyWithFallbacks, size })
            .dx(position.x)
            .dy(position.y)
            .attr({
              direction: direction,
              'text-anchor': textAnchor
            });
        }

        if (color !== undefined) {
          svgElement.fill(color.toHexRGB());
        }

        // Debug: draw a point at the origin of the run.
        // group.circle(2)
        //   .fill(COLOR_RED)
        //   .move(position.x, position.y);
      });
    } else {
      const useFallbacks = this.fontExportMethod === FontExportMethod.FALLBACKS;

      const familyCounts = new FrequencyCounter<string>();
      const fontSizeCounts = new FrequencyCounter<number>();
      paragraph.runs.forEach(run => {
        this.referenceFont(run.familyName);
        familyCounts.add(run.familyName);
        fontSizeCounts.add(run.size);
      });

      let defaultFamily = expectDefined(familyCounts.top);
      if (this.useMonochromeEmoji && defaultFamily.toLowerCase().includes('emoji')) {
        defaultFamily = 'Noto Emoji';
      }

      const fontFamily = useFallbacks
        ? this.getUsedFont(defaultFamily).familyWithFallbacks
        : defaultFamily;
      const defaultSize = expectDefined(fontSizeCounts.top);
      group.attr({
        direction: direction,
        'text-anchor': textAnchor,
        'font-family': fontFamily,
        'font-size': defaultSize,
        'font-weight': isBold ? 700 : 400,
        'font-style': isItalic ? 'italic' : 'normal'
      });

      paragraph.runs.forEach(run => {
        const text = run.text;
        const position = new Point(run.position);
        const c = new Color(run.color);
        const color = c.equals(defaultColor) ? undefined : c;

        let family: string | undefined = run.familyName;
        if (this.useMonochromeEmoji && family.toLowerCase().includes('emoji')) {
          family = 'Noto Emoji';
        }
        if (family === defaultFamily) {
          family = undefined;
        }
        if (family !== undefined && useFallbacks) {
          family = this.getUsedFont(family).familyWithFallbacks;
        }
        const size = run.size !== defaultSize ? run.size : undefined;

        const svgElement = group
          .plain(text)
          .font({ family, size })
          .dx(position.x)
          .dy(position.y);

        if (color !== undefined) {
          svgElement.fill(color.toHexRGB());
        }

        // Debug: draw a point at the origin of the run.
        // group.circle(2)
        //   .fill(COLOR_RED)
        //   .move(position.x, position.y);
      });
    }

    // Debug: draw a point at the origin of the paragraph.
    // this.draw.circle(2)
    //   .fill(COLOR_RED)
    //   .move(point.x, point.y);
  }

  clear(color: Color): void {
    // console.warn(`clear(${color.toString()})`);
    this.mainGroup
      .rect(this.bounds.width, this.bounds.height)
      .fill(color.toHexRGB())
      .move(this.bounds.left, this.bounds.top);
  }

  save(): void {
    console.warn(`save()`);
  }

  restore(): void {
    console.warn(`restore()`);
  }

  translate(p: Point): void {
    console.warn(`translate(${p.toString()})`);
  }

  scale(value: number): void {
    console.warn(`scale(${fixedNumber(value, 3)})`);
  }

  concat(matrix: Matrix): void {
    console.warn(`concat(${matrix.toString()})`);
  }

  userSpaceBounds(): { bounds: Rect; scale: number } {
    return { bounds: this.bounds, scale: 1 };
  }

  drawLine(line: LineSegment, paint: VisualPaint | undefined) {
    const svgLine = this.mainGroup.line(line.p1.x, line.p1.y, line.p2.x, line.p2.y);

    if (paint !== undefined) {
      svgLine.stroke(svgStrokeForPaint(paint));
    }
  }

  drawRect(r: Rect, opts?: DrawRectOpts): void {
    const { cornerRadius, fill, stroke } = opts ?? {};
    let element: SvgElement;

    if (cornerRadius !== undefined) {
      const cr = normalizeCornerRadius(cornerRadius);
      if (isZeroCornerRadius(cr)) {
        element = this.mainGroup.rect(r.width, r.height).move(r.left, r.top);
      } else if (isUniformCornerRadius(cr)) {
        const rx = cr.topLeft.width;
        const ry = cr.topLeft.height;
        element = this.mainGroup.rect(r.width, r.height).move(r.left, r.top);
        if (rx > 0 || ry > 0) {
          (element as SvgRect).radius(rx, ry);
        }
      } else {
        const path = pathForRoundedRect(r, cr);
        element = this.mainGroup.path(path);
      }
    } else {
      element = this.mainGroup.rect(r.width, r.height).move(r.left, r.top);
    }
    if (fill !== undefined) {
      // const cssColor = fill.toCSS(2);
      // console.log(`fill: ${cssColor}`);
      const cssColor = fill.toHexRGB();
      element.fill(cssColor);

      if (fill.alpha < 1) {
        element.opacity(fixedFloat(fill.alpha, SVG_PRECISION));
      }
    } else {
      element.fill('none');
    }
    if (stroke) {
      element.stroke({
        color: stroke.color.toHexRGB(),
        width: stroke.width
      });
    }
  }

  drawImage(image: Image, bounds: Rect, opts?: DrawImageOpts): void {
    const { objectFit = ObjectFit.FILL, objectPosition, opacity = 1.0 } = opts ?? {};

    // Check if this is an SVG source that should be embedded as vector
    if (image.source?.scalable && image.source instanceof SVGImageSource) {
      this.drawSvgImage(image.source, bounds, {
        objectFit,
        objectPosition,
        opacity
      });
    } else {
      this.drawBitmapImage(image, bounds, {
        objectFit,
        objectPosition,
        opacity
      });
    }
  }

  private drawSvgImage(
    svgSource: SVGImageSource,
    bounds: Rect,
    opts: DrawImageOpts
  ): void {
    const { objectFit = ObjectFit.FILL, objectPosition, opacity = 1.0 } = opts;

    // Use SVG content as key for deduplication
    const svgContent = svgSource.svgContent;
    let svgId: string;

    if (this.svgDefsMap.has(svgContent)) {
      const existingId = this.svgDefsMap.get(svgContent);
      if (!existingId) {
        throw new Error('SVG content exists in map but ID is undefined');
      }
      svgId = existingId;
    } else {
      this.svgDefsCounter++;
      svgId = `svg-${this.svgDefsCounter}`;
      this.svgDefsMap.set(svgContent, svgId);

      // Create SVG definition in defs
      const defs = this.defs;

      // Create a group to contain the SVG content with proper isolation
      const svgGroup = defs.group().attr('id', svgId);

      // Extract inner content by removing outer <svg> tags using stripRootAndPrefixClasses
      const innerContent = stripRootAndPrefixClasses(svgContent, svgId);

      // Add the inner SVG content to the group
      svgGroup.svg(innerContent);
    }

    // Calculate object fit layout using the SVG source's intrinsic size
    const layout = calculateObjectFitLayout(
      svgSource.intrinsicSize,
      bounds.size,
      objectFit,
      objectPosition
    );

    // Calculate transform for the use element
    const scaleX = layout.destRect.width / svgSource.intrinsicSize.width;
    const scaleY = layout.destRect.height / svgSource.intrinsicSize.height;
    const translateX = bounds.left + layout.destRect.left;
    const translateY = bounds.top + layout.destRect.top;

    // Create use element with transform
    const useElement = this.mainGroup.use(`#${svgId}`).attr({
      href: `#${svgId}`,
      'xlink:href': `#${svgId}`,
      transform: `translate(${fixedNumber(translateX, SVG_PRECISION)} ${fixedNumber(translateY, SVG_PRECISION)}) scale(${fixedNumber(scaleX, SVG_PRECISION)} ${fixedNumber(scaleY, SVG_PRECISION)})`
    });

    // Apply opacity if needed
    if (opacity < 1) {
      useElement.attr('style', `opacity: ${fixedFloat(opacity, 1)};`);
    }
  }

  private drawBitmapImage(image: Image, bounds: Rect, opts: DrawImageOpts): void {
    const { objectFit = ObjectFit.FILL, objectPosition, opacity = 1.0 } = opts;

    // Use the Image object itself as the key for deduplication
    let imageId: string;
    if (this.imageDefsMap.has(image)) {
      const existingId = this.imageDefsMap.get(image);
      if (existingId) {
        imageId = existingId;
      } else {
        throw new Error('Image exists in map but ID is undefined');
      }
    } else {
      this.imageDefsCounter++;
      imageId = `image-${this.imageDefsCounter}`;
      this.imageDefsMap.set(image, imageId);

      // Create image definition in defs
      const defs = this.defs;
      const width = image.width;
      const height = image.height;
      const base64 = this.getBase64(image);
      const dataUrl = `data:image/png;base64,${base64}`;

      // Add xlink namespace to root SVG for Adobe Illustrator compatibility
      if (!this.draw.attr('xmlns:xlink')) {
        this.draw.attr('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      }

      // For image definitions, only use xlink:href to avoid duplicating data
      const imageElement = defs.image();
      imageElement.attr({
        id: imageId,
        width: width,
        height: height
      });

      // Explicitly set only xlink:href and remove any default href attribute
      imageElement.node.removeAttribute('href');
      imageElement.node.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'xlink:href',
        dataUrl
      );
    }

    // Calculate object fit layout
    const layout = calculateObjectFitLayout(
      image.size,
      bounds.size,
      objectFit,
      objectPosition
    );

    // Calculate transform for the use element
    const scaleX = layout.destRect.width / image.width;
    const scaleY = layout.destRect.height / image.height;
    const translateX = bounds.left + layout.destRect.left;
    const translateY = bounds.top + layout.destRect.top;

    // Create use element with transform
    const useElement = this.mainGroup.use(imageId).attr({
      href: `#${imageId}`,
      'xlink:href': `#${imageId}`,
      transform: `translate(${fixedNumber(translateX, SVG_PRECISION)} ${fixedNumber(translateY, SVG_PRECISION)}) scale(${fixedNumber(scaleX, SVG_PRECISION)} ${fixedNumber(scaleY, SVG_PRECISION)})`
    });

    // Apply opacity if needed
    if (opacity < 1) {
      useElement.attr('style', `opacity: ${fixedFloat(opacity, 1)};`);
    }
  }

  private getBase64(image: Image): string {
    const bytes = image.encodeToBytes();
    if (!bytes) {
      throw new Error('Failed to encode image to bytes');
    }
    return encodeBase64(bytes);
  }

  drawCircle(center: Point, radius: number, paint: VisualPaint): void {
    const circle = this.mainGroup.circle(radius * 2).center(center.x, center.y);

    switch (paint.style) {
      case PaintStyle.FILL:
        circle.fill(svgFillForPaint(paint));
        break;
      case PaintStyle.STROKE:
        circle.fill('none');
        circle.stroke(svgStrokeForPaint(paint));
        break;
    }
  }

  drawShape(shape: Shape, paint: VisualPaint): void {
    const path = this.mainGroup.path(shape.svgPath);

    switch (paint.style) {
      case PaintStyle.FILL:
        path.fill(svgFillForPaint(paint));
        break;
      case PaintStyle.STROKE:
        path.stroke(svgStrokeForPaint(paint));
        break;
    }
  }

  withGroup(
    opts: Partial<VisualGroupOpts> | undefined,
    fn: (paint: VisualPaint | undefined) => void
  ): void {
    const color = opts?.color?.toHexRGB();
    const width = opts?.strokeWidth;
    const colorOpacity =
      opts?.color !== undefined ? fixedFloat(opts.color.alpha, SVG_PRECISION) : undefined;
    const groupOpacity =
      opts?.opacity !== undefined && opts.opacity !== 1.0
        ? fixedFloat(opts.opacity, SVG_PRECISION)
        : undefined;
    const oldDraw = this.mainGroup;
    const group = this.mainGroup.group();
    if (opts?.id !== undefined) {
      group.attr('id', opts.id);
    }
    if (opts?.attrs !== undefined) {
      group.attr(opts.attrs);
    }
    if (opts?.title !== undefined) {
      group.attr('aria-label', opts.title);
      group.element('title').words(opts.title);
    }
    if (opts?.desc !== undefined) {
      group.attr('aria-description', opts.desc);
      // group.element('desc').words(opts.desc);
    }
    if (opts?.offset !== undefined && !opts.offset.equals(Point.ZERO)) {
      group.attr(
        'transform',
        `translate(${fixedNumber(opts.offset.x, SVG_PRECISION)}, ${fixedNumber(opts.offset.y, SVG_PRECISION)})`
      );
    }
    if (groupOpacity !== undefined) {
      group.attr('opacity', groupOpacity);
    }
    group.stroke({ color, width, opacity: colorOpacity });
    this.mainGroup = group;
    fn(undefined);
    this.mainGroup = oldDraw;
  }

  private addPrefix(id: string): string {
    if (this.idPrefix.length > 0) {
      return `${this.idPrefix}-${id}`;
    }
    return id;
  }

  withClip(
    bounds: Rect,
    fn: () => void,
    cornerRadius?: number | Partial<CornerRadiusOpts>
  ): void {
    this.clipPathCounter++;
    const clipId = this.addPrefix(`clip-${this.clipPathCounter}`);

    // Create clipPath element in defs
    const clipPath = this.defs.element('clipPath').attr('id', clipId);

    // Create the clipping shape based on corner radius
    if (cornerRadius !== undefined) {
      const cr = normalizeCornerRadius(cornerRadius);
      if (isZeroCornerRadius(cr)) {
        const rect = this.mainGroup
          .rect(bounds.width, bounds.height)
          .move(bounds.left, bounds.top);
        clipPath.add(rect);
      } else if (isUniformCornerRadius(cr)) {
        const rx = cr.topLeft.width;
        const ry = cr.topLeft.height;
        const rect = this.mainGroup
          .rect(bounds.width, bounds.height)
          .move(bounds.left, bounds.top);
        if (rx > 0 || ry > 0) {
          rect.radius(rx, ry);
        }
        clipPath.add(rect);
      } else {
        const pathData = pathForRoundedRect(bounds, cr);
        const path = this.mainGroup.path(pathData);
        clipPath.add(path);
      }
    } else {
      const rect = this.mainGroup
        .rect(bounds.width, bounds.height)
        .move(bounds.left, bounds.top);
      clipPath.add(rect);
    }

    // Create group with clip-path attribute
    const oldMainGroup = this.mainGroup;
    const group = this.mainGroup.group().attr('clip-path', `url(#${clipId})`);
    this.mainGroup = group;

    try {
      fn();
    } finally {
      this.mainGroup = oldMainGroup;
      // Note: Don't remove the clipPath - it's needed for the final SVG output
    }
  }
}

function svgStrokeForPaint(paint: VisualPaint): StrokeData {
  const color = paint.color?.toHexRGB();
  const width = paint.strokeWidth;
  const opacity =
    paint.color !== undefined ? fixedFloat(paint.color.alpha, SVG_PRECISION) : undefined;
  let linecap: string | undefined;
  switch (paint.strokeCap) {
    case StrokeCap.BUTT:
    case undefined:
      linecap = undefined;
      break;
    case StrokeCap.ROUND:
      linecap = 'round';
      break;
    case StrokeCap.SQUARE:
      linecap = 'square';
      break;
  }
  return { color, width, opacity, linecap };
}

function svgFillForPaint(paint: VisualPaint): FillData {
  const color = paint.color?.toHexRGB();
  const opacity =
    paint.color !== undefined ? fixedFloat(paint.color.alpha, SVG_PRECISION) : undefined;
  return { color, opacity };
}

export function pathForRoundedRect(
  rect: Rect,
  radius: NormalizedCornerRadiusOpts
): string {
  const tl = radius.topLeft;
  const tr = radius.topRight;
  const br = radius.bottomRight;
  const bl = radius.bottomLeft;
  const commands: (string | number)[] = [
    'M',
    rect.left + tl.width,
    rect.top,
    'H',
    rect.right - tr.width,
    'A',
    tr.width,
    tr.height,
    0,
    0,
    1,
    rect.right,
    rect.top + tr.height,
    'V',
    rect.bottom - br.height,
    'A',
    br.width,
    br.height,
    0,
    0,
    1,
    rect.right - br.width,
    rect.bottom,
    'H',
    rect.left + bl.width,
    'A',
    bl.width,
    bl.height,
    0,
    0,
    1,
    rect.left,
    rect.bottom - bl.height,
    'V',
    rect.top + tl.height,
    'A',
    tl.width,
    tl.height,
    0,
    0,
    1,
    rect.left + tl.width,
    rect.top,
    'Z'
  ];
  return composeSvgPathData(commands, SERIALIZATION_PRECISION);
}
